import { ChevronLeft, ChevronRight, Download, Eye, LayoutGrid, List, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { DEFAULT_MAX_FILE_SIZE_BYTES } from '@/components/common/FilePicker';
import DocFolder from '@/components/home/cards/DocFolder';
import { useFileUploadWithPresigning } from '@/hooks/useFileUploadWithPresigning';
import { CreateDocumentRecord, DocumentRecordType } from '@/models/DocumentRecord';

/**
 * Records & Files as one screen: the folders ride a slider along the top (the same glass-and-paper DocFolder, the
 * open one on the raised tile), and the open folder's contents sit below in a viewer made of the same materials:
 * the folder's dark pocket, with a tab carrying its name, and the documents as the same paper sheets, each with its
 * type on a round badge. Grid or list; preview, download, rename and delete on hover.
 */
export interface FinderFile {
  id: string;
  name: string;
  createdAt?: number;
}

export interface FinderFolder {
  id: string;
  label: string;
  files: FinderFile[];
}

const ext = (name: string) => {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i + 1).toUpperCase().slice(0, 4) : 'FILE';
};
const base = (name: string) => {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
};
const toMs = (ts?: number) => (!ts ? 0 : ts < 1e12 ? ts * 1000 : ts);
const fmt = (ts?: number) =>
  ts ? new Date(toMs(ts)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const CSS = `
@keyframes hxViewerIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.hx-rb-slider::-webkit-scrollbar { display: none; }
.hx-rb-zoom { -webkit-appearance: none; appearance: none; width: 110px; height: 16px; background: transparent; cursor: pointer; }
.hx-rb-zoom::-webkit-slider-runnable-track { height: 2px; border-radius: 1px; background: rgba(var(--tx-ink-rgb), 0.18); }
.hx-rb-zoom::-moz-range-track { height: 2px; border-radius: 1px; background: rgba(var(--tx-ink-rgb), 0.18); }
.hx-rb-zoom::-webkit-slider-thumb { -webkit-appearance: none; width: 12px; height: 12px; margin-top: -5px; border-radius: 50%;
  background: #f4f3ef; box-shadow: 0 1px 4px rgba(0,0,0,0.5); }
.hx-rb-zoom::-moz-range-thumb { width: 12px; height: 12px; border: 0; border-radius: 50%; background: #f4f3ef; }
.hx-fw-folder { transition: background 0.12s ease, color 0.12s ease; }
.hx-fw-folder:hover { background: var(--tx-fill); color: var(--tx-text); }
.hx-fw-tile { transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1); }
.hx-fw-tile:hover { transform: translateY(-3px); }
.hx-fw-tile .hx-fw-actions, .hx-fw-row .hx-fw-actions { opacity: 0; transition: opacity 0.15s ease; }
.hx-fw-tile:hover .hx-fw-actions, .hx-fw-tile:focus-within .hx-fw-actions,
.hx-fw-row:hover .hx-fw-actions, .hx-fw-row:focus-within .hx-fw-actions { opacity: 1; }
.hx-fw-row { transition: background 0.12s ease; }
.hx-add-file:hover { background: var(--tx-fill-2) !important; }
.hx-fw-row:hover { background: var(--tx-fill); }
.hx-fw-act { transition: background 0.12s ease, color 0.12s ease; }
.hx-fw-act:hover { background: rgba(var(--tx-ink-rgb), 0.12); color: var(--tx-text); }
.hx-fw-act[data-danger]:hover { color: #ff8a8a; }
@media (prefers-reduced-motion: reduce) { .hx-fw-tile:hover { transform: none; } }
`;

const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'grid',
  placeItems: 'center',
  border: 0,
  borderRadius: 8,
  background: 'transparent',
  color: 'var(--tx-muted)',
  cursor: 'pointer',
};

export const RecordsBrowser = ({
  folders,
  activeId,
  onSelect,
  onPreview,
  onDownload,
  onDelete,
  onRename,
  onUpload,
  propertyId,
  onCreate,
  pickNonce = 0,
  isMobile = false,
}: {
  folders: FinderFolder[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onPreview: (e: React.MouseEvent, id: string) => void;
  onDownload: (e: React.MouseEvent, id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onRename: (id: string, name: string) => void;
  onUpload?: () => void;
  /** adding files in place: drop onto the open folder (or pick), name it, choose its folder, add */
  propertyId?: string;
  onCreate?: (values: CreateDocumentRecord) => void;
  /** bump to open the file picker from outside (the page's Upload button) */
  pickNonce?: number;
  isMobile?: boolean;
}) => {
  const [view, setView] = useState<'grid' | 'list'>('grid');
  // the grid's zoom: each paper's minimum width, as Finder's icon-size slider
  // 27": smaller papers by default and more air around the folder row
  const wide = typeof window !== 'undefined' && window.innerWidth >= 1680 && window.innerHeight >= 1000;
  const [zoom, setZoom] = useState(wide ? 120 : 140);
  const [renaming, setRenaming] = useState<{ id: string; value: string; ext: string } | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  // the tab's width, so the pocket's lit top edge can pick up exactly where the tab's shoulder lands
  const tabRef = useRef<HTMLDivElement | null>(null);
  const [tabW, setTabW] = useState(0);
  useEffect(() => {
    const el = tabRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setTabW(el.offsetWidth));
    ro.observe(el);
    setTabW(el.offsetWidth);
    return () => ro.disconnect();
  }, [activeId]);
  const folder = folders.find((f) => f.id === activeId) ?? null;
  const realFolders = folders.filter((f) => !f.id.startsWith('_'));

  // ── adding a file, in place: no dialog. Drop or pick → it uploads as a paper in the folder → name, folder, Add ──
  const [staged, setStaged] = useState<{ file: File; name: string; ext: string; folderId: string; docId?: string } | null>(
    null,
  );
  const [dropError, setDropError] = useState('');
  const upload = useFileUploadWithPresigning(false, propertyId ?? '');
  const canAdd = Boolean(propertyId && onCreate);
  const stage = (file: File) => {
    const i = file.name.lastIndexOf('.');
    setDropError('');
    setStaged({
      file,
      name: i > 0 ? file.name.slice(0, i) : file.name,
      ext: i > 0 ? file.name.slice(i) : '',
      folderId: folder && !folder.id.startsWith('_') ? folder.id : (realFolders[0]?.id ?? ''),
    });
    setView('grid');
    upload.startFileUpload([file]);
  };
  const dropzone = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    maxSize: DEFAULT_MAX_FILE_SIZE_BYTES,
    noClick: true,
    noKeyboard: true,
    disabled: !canAdd || Boolean(staged),
    onDrop: (accepted, rejected) => {
      if (rejected.length) {
        const code = rejected[0].errors[0]?.code;
        setDropError(
          code === 'file-too-large' ? 'That file is over 25 MB.' : 'PDF, DOCX, JPG or PNG files only, one at a time.',
        );
        return;
      }
      if (accepted[0]) stage(accepted[0]);
    },
  });
  useEffect(() => {
    if (upload.isSuccess && upload.result[0] && staged && !staged.docId) {
      setStaged({ ...staged, docId: upload.result[0].id });
    }
  }, [upload.isSuccess, upload.result]);
  useEffect(() => {
    if (pickNonce && canAdd) dropzone.open();
  }, [pickNonce]);
  const addStaged = () => {
    if (!staged?.docId || !staged.folderId || !onCreate) return;
    onCreate({
      parentId: staged.folderId,
      documentId: staged.docId,
      name: `${staged.name.trim() || 'Document'}${staged.ext}`,
      type: DocumentRecordType.File,
    });
    if (staged.folderId !== activeId) onSelect(staged.folderId);
    setStaged(null);
  };

  useEffect(() => setRenaming(null), [activeId]);
  // keep the open folder in view in the slider
  useEffect(() => {
    sliderRef.current
      ?.querySelector<HTMLElement>(`[data-folder="${activeId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeId]);

  const files = useMemo(
    () => [...(folder?.files ?? [])].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt)),
    [folder],
  );
  const newest = files.length ? toMs(files[0].createdAt) : 0;

  const commitRename = () => {
    if (!renaming) return;
    const name = renaming.value.trim();
    if (name) onRename(renaming.id, `${name}${renaming.ext}`);
    setRenaming(null);
  };
  const startRename = (e: React.MouseEvent, f: FinderFile) => {
    e.stopPropagation();
    const i = f.name.lastIndexOf('.');
    setRenaming({ id: f.id, value: i > 0 ? f.name.slice(0, i) : f.name, ext: i > 0 ? f.name.slice(i) : '' });
  };
  const slide = (dir: number) => sliderRef.current?.scrollBy({ left: dir * 460, behavior: 'smooth' });

  const actions = (f: FinderFile, onPaper: boolean) => (
    <div
      className="hx-fw-actions"
      style={{
        display: 'flex',
        gap: onPaper ? 4 : 2,
        padding: onPaper ? 5 : 0,
        borderRadius: 10,
        background: onPaper ? 'rgba(28,28,27,0.78)' : 'transparent',
        backdropFilter: onPaper ? 'blur(10px)' : undefined,
        WebkitBackdropFilter: onPaper ? 'blur(10px)' : undefined,
      }}>
      <button type="button" className="hx-fw-act" style={iconBtn} aria-label="Preview" title="Preview" onClick={(e) => onPreview(e, f.id)}>
        <Eye size={15} strokeWidth={1.6} />
      </button>
      <button type="button" className="hx-fw-act" style={iconBtn} aria-label="Download" title="Download" onClick={(e) => onDownload(e, f.id)}>
        <Download size={15} strokeWidth={1.6} />
      </button>
      <button type="button" className="hx-fw-act" style={iconBtn} aria-label="Rename" title="Rename" onClick={(e) => startRename(e, f)}>
        <Pencil size={14} strokeWidth={1.6} />
      </button>
      <button type="button" className="hx-fw-act" data-danger style={iconBtn} aria-label="Delete" title="Delete" onClick={(e) => onDelete(e, f.id)}>
        <Trash2 size={14} strokeWidth={1.6} />
      </button>
    </div>
  );

  const nameNode = (f: FinderFile, size: number) =>
    renaming?.id === f.id ? (
      <input
        autoFocus
        value={renaming.value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
        onBlur={commitRename}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitRename();
          if (e.key === 'Escape') setRenaming(null);
        }}
        style={{
          width: '100%',
          height: 26,
          padding: '0 8px',
          border: 0,
          borderRadius: 7,
          outline: 'none',
          background: 'var(--tx-fill-2)',
          color: 'var(--tx-text)',
          fontFamily: 'inherit',
          fontSize: size,
          fontWeight: 500,
        }}
      />
    ) : (
      <span
        style={{
          fontSize: size,
          fontWeight: 500,
          lineHeight: 1.3,
          color: 'var(--tx-text)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          wordBreak: 'break-word',
        }}>
        {base(f.name)}
      </span>
    );

  // the paper, exactly as it sits in the folders: warm white, faint lines, the type on a round badge
  const paper = (f: FinderFile, small = false) => (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: small ? 5 : 10,
        padding: small ? 0 : '16% 14% 0',
        boxSizing: 'border-box',
        background: 'linear-gradient(180deg, #f6f5f1 0%, #e2e0da 100%)',
        boxShadow: small ? 'none' : '0 1px 0 rgba(255,255,255,0.75) inset, 0 16px 30px -16px rgba(0,0,0,0.75)',
        overflow: 'hidden',
      }}>
      {!small && (
        <>
          <span style={{ display: 'block', height: 6, width: '70%', borderRadius: 3, background: 'rgba(38,38,37,0.18)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            {[92, 84, 90, 76, 88, 64, 80].map((w, i) => (
              <span key={i} style={{ height: 3, width: `${w}%`, borderRadius: 2, background: 'rgba(38,38,37,0.1)' }} />
            ))}
          </div>
        </>
      )}
      <span
        style={{
          position: 'absolute',
          left: small ? '50%' : 10,
          bottom: small ? 4 : 10,
          transform: small ? 'translateX(-50%)' : undefined,
          width: small ? 20 : 28,
          height: small ? 20 : 28,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: '#2f2f2e',
          color: '#f4f3ef',
          fontSize: small ? 6 : 7.5,
          fontWeight: 700,
          letterSpacing: '0.03em',
        }}>
        {ext(f.name)}
      </span>
    </div>
  );

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <style>{CSS}</style>

      {/* the folders, a slider along the top */}
      <div style={{ position: 'relative', flexShrink: 0, paddingTop: isMobile ? 6 : wide ? 30 : 14 }}>
        {/* edge arrows, centred on the folders */}
        {!isMobile &&
          folders.length > 4 &&
          [-1, 1].map((d) => (
            <button
              key={d}
              type="button"
              aria-label={d < 0 ? 'Previous folders' : 'Next folders'}
              onClick={() => slide(d)}
              style={{
                ...iconBtn,
                position: 'absolute',
                top: '50%',
                [d < 0 ? 'left' : 'right']: -6,
                transform: 'translateY(-50%)',
                zIndex: 5,
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'var(--tx-dialog)',
                boxShadow: 'var(--tx-shadow-sm)',
                color: 'var(--tx-text)',
              }}>
              {d < 0 ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </button>
          ))}
        <div
          ref={sliderRef}
          className="hx-rb-slider"
          style={{
            display: 'flex',
            gap: isMobile ? 8 : wide ? 24 : 12,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            padding: wide ? '4px 2px 24px' : '4px 2px 14px',
            maskImage: 'linear-gradient(90deg, transparent 0, #000 16px, #000 calc(100% - 40px), transparent)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 16px, #000 calc(100% - 40px), transparent)',
          }}>
          {folders.map((f) => (
            <div key={f.id} data-folder={f.id} style={{ flex: `0 0 ${isMobile ? 168 : wide ? 250 : 228}px`, scrollSnapAlign: 'start' }}>
              <DocFolder
                active={f.id === activeId}
                onClick={() => onSelect(f.id)}
                sheets={[...f.files]
                  .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))
                  .slice(0, 3)
                  .reverse()
                  .map((fi) => ({ title: fi.name, meta: fmt(fi.createdAt) }))}
                badges={[...new Set(f.files.map((fi) => ext(fi.name)))]}
                label={f.label}
                count={f.files.length ? `${f.files.length} ${f.files.length === 1 ? 'file' : 'files'}` : undefined}
                sublabel={f.files.length ? undefined : 'Empty'}
              />
            </div>
          ))}
        </div>
      </div>

      {/* the viewer: the open folder's pocket, its name on the tab */}
      {folder && (
        <div
          key={folder.id}
          style={{
            marginTop: isMobile ? 10 : wide ? 40 : 22,
            flex: 1,
            minHeight: isMobile ? 320 : 0,
            display: 'flex',
            flexDirection: 'column',
            animation: 'hxViewerIn 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          }}>
          {/* the tab, shaped like the folder fronts above: raised on the left, easing down into the pocket */}
          <div
            ref={tabRef}
            style={{ display: 'flex', alignItems: 'stretch', alignSelf: 'flex-start', maxWidth: '78%', flexShrink: 0 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 12,
                minWidth: 0,
                padding: isMobile ? '15px 8px 13px 20px' : '19px 10px 16px 26px',
                // the pocket's own slate (the folder front's shape, not its colour), lit along its top edge
                borderRadius: '16px 0 0 0',
                background: 'linear-gradient(180deg, #2d2d2c 0%, #262626 100%)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 1px 0 0 rgba(255,255,255,0.04)',
              }}>
              <span
                style={{
                  fontSize: isMobile ? 15 : 17,
                  fontWeight: 400,
                  letterSpacing: '-0.015em',
                  color: 'var(--tx-text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {folder.label}
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-faint)', whiteSpace: 'nowrap' }}>
                {folder.files.length
                  ? `${folder.files.length} ${folder.files.length === 1 ? 'file' : 'files'} · ${fmt(newest)}`
                  : 'Empty'}
              </span>
            </div>
            <svg aria-hidden viewBox="0 0 120 56" preserveAspectRatio="none" style={{ width: isMobile ? 84 : 120, flexShrink: 0, display: 'block' }}>
              <defs>
                <linearGradient id="hx-rb-tab" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#2d2d2c" />
                  <stop offset="1" stopColor="#262626" />
                </linearGradient>
              </defs>
              {/* the folder front's shoulder, scaled 1:1 from DocFolder: a rounded crest, a steep run, a wide fillet */}
              <path d="M0 0 H2 Q29.6 0 48 15.6 L78 41.6 Q93.8 56 118 56 H120 V56 H0 Z" fill="url(#hx-rb-tab)" />
              <path
                d="M0 0.5 H2 Q29.4 0.5 47.7 16 L77.7 42 Q93.6 55.5 118 55.5 H120"
                fill="none"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>
          <div
            {...dropzone.getRootProps()}
            style={{
              position: 'relative',
              outline: 'none',
              borderRadius: '0 22px 22px 22px',
              background: '#262626',
              // no top hairline (it read as a divider under the tab); the lit edge is drawn below, right of the tab
              boxShadow: '0 24px 50px -30px rgba(0,0,0,0.8)',
              padding: isMobile ? '16px 14px 20px' : '20px 24px 26px',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarWidth: 'none',
            }}>
            <input {...dropzone.getInputProps()} />
            {/* the folder front's lit edge, continued: from the tab's shoulder across the top and round the corner */}
            {tabW > 0 && (
              <>
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: tabW - 1,
                    right: 22,
                    height: 1,
                    background: 'rgba(255,255,255,0.1)',
                    pointerEvents: 'none',
                  }}
                />
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 22,
                    height: 22,
                    borderTopRightRadius: 22,
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    borderRight: '1px solid rgba(255,255,255,0.1)',
                    WebkitMaskImage: 'linear-gradient(180deg, #000 30%, transparent)',
                    maskImage: 'linear-gradient(180deg, #000 30%, transparent)',
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                  }}
                />
              </>
            )}
            {dropzone.isDragActive && (
              <div
                style={{
                  position: 'absolute',
                  inset: 10,
                  zIndex: 20,
                  borderRadius: 18,
                  border: '1.5px dashed var(--tx-lime)',
                  background: 'rgba(38,38,38,0.86)',
                  display: 'grid',
                  placeItems: 'center',
                  pointerEvents: 'none',
                }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)' }}>
                    Drop to file in {folder.label}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 6 }}>PDF, DOCX, JPG or PNG · up to 25 MB</div>
                </div>
              </div>
            )}
            {dropError && (
              <div style={{ fontSize: 12, color: '#ff8a8a', marginBottom: 12 }}>{dropError}</div>
            )}
            {files.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                {view === 'grid' && !isMobile && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--tx-faint)' }}>
                    <span style={{ width: 8, height: 10, borderRadius: 2, border: '1.2px solid currentColor' }} />
                    <input
                      type="range"
                      className="hx-rb-zoom"
                      aria-label="Thumbnail size"
                      min={100}
                      max={240}
                      step={10}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                    />
                    <span style={{ width: 12, height: 15, borderRadius: 3, border: '1.2px solid currentColor' }} />
                  </label>
                )}
                <div style={{ display: 'flex', padding: 3, borderRadius: 10, background: 'rgba(var(--tx-ink-rgb), 0.06)', gap: 2 }}>
                  {(['grid', 'list'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      aria-label={v === 'grid' ? 'Grid view' : 'List view'}
                      aria-pressed={view === v}
                      onClick={() => setView(v)}
                      style={{
                        ...iconBtn,
                        height: 28,
                        borderRadius: 7,
                        background: view === v ? 'var(--tx-sel-bg)' : 'transparent',
                        boxShadow: view === v ? 'var(--tx-sel-shadow)' : 'none',
                        color: view === v ? 'var(--tx-text)' : 'var(--tx-muted)',
                      }}>
                      {v === 'grid' ? <LayoutGrid size={14} strokeWidth={1.7} /> : <List size={15} strokeWidth={1.7} />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {files.length === 0 && !staged ? (
              <div
                style={{
                  minHeight: 220,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  textAlign: 'center',
                }}>
                {/* the empty folder's sleeve is the way in: a dashed sheet you click to pick a file or drop one on (the
                    same dashed tile as Today's "Add a photo" and the sleeve in the records grid), two faint sheets
                    fanned behind it the way the folders show their papers. No upload button */}
                <style>{`
                  .hx-sleeve-add { position: relative; width: 168px; aspect-ratio: 3 / 4; padding: 0; border: 0; margin-top: 40px;
                    background: transparent; cursor: pointer; font: inherit; }
                  .hx-sleeve-add .ghost { position: absolute; inset: 0; border-radius: 14px;
                    border: 1.5px dashed rgba(var(--tx-ink-rgb), 0.12); transition: transform 0.45s cubic-bezier(0.32, 0.72, 0, 1); }
                  .hx-sleeve-add .ghost.l { transform: rotate(-7deg) translate(-10px, 4px); }
                  .hx-sleeve-add .ghost.r { transform: rotate(6deg) translate(10px, 2px); }
                  .hx-sleeve-add .face { position: absolute; inset: 0; border-radius: 14px; display: flex; flex-direction: column;
                    align-items: center; justify-content: center; gap: 8px; border: 1.5px dashed rgba(var(--tx-ink-rgb), 0.28);
                    background: var(--tx-glass-panel); color: var(--tx-muted);
                    transition: transform 0.45s cubic-bezier(0.32, 0.72, 0, 1), border-color 0.15s ease, color 0.15s ease; }
                  .hx-sleeve-add .plus { width: 44px; height: 44px; border-radius: 50%; display: grid; place-items: center;
                    background: var(--tx-fill); color: var(--tx-text); transition: background 0.15s ease; }
                  .hx-sleeve-add:hover .face, .hx-sleeve-add:focus-visible .face, .hx-sleeve-add.drag .face {
                    transform: translateY(-4px); border-color: rgba(var(--tx-ink-rgb), 0.5); color: var(--tx-text); }
                  .hx-sleeve-add:hover .plus, .hx-sleeve-add.drag .plus { background: var(--tx-fill-2); }
                  .hx-sleeve-add:hover .ghost.l, .hx-sleeve-add.drag .ghost.l { transform: rotate(-11deg) translate(-18px, 6px); }
                  .hx-sleeve-add:hover .ghost.r, .hx-sleeve-add.drag .ghost.r { transform: rotate(10deg) translate(18px, 4px); }
                  .hx-sleeve-add:focus-visible { outline: 2px solid var(--tx-focus); outline-offset: 6px; border-radius: 14px; }
                  @media (prefers-reduced-motion: reduce) { .hx-sleeve-add .face, .hx-sleeve-add .ghost { transition: none; } }
                `}</style>
                {canAdd || onUpload ? (
                  <button
                    type="button"
                    className={`hx-sleeve-add${dropzone.isDragActive ? ' drag' : ''}`}
                    aria-label={`Add a file to ${folder.label}`}
                    onClick={() => (canAdd ? dropzone.open() : onUpload?.())}>
                    <span className="ghost l" aria-hidden />
                    <span className="ghost r" aria-hidden />
                    <span className="face">
                      <span className="plus">
                        <Plus size={20} strokeWidth={1.8} />
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em' }}>Add a file</span>
                      {canAdd && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)' }}>or drop one here</span>}
                    </span>
                  </button>
                ) : (
                  <span style={{ width: 54, height: 70, borderRadius: 8, border: '1.5px dashed rgba(var(--tx-ink-rgb), 0.28)' }} />
                )}
                <div style={{ marginTop: 32, fontSize: 13, fontWeight: 500, color: 'var(--tx-text)' }}>
                  Nothing in {folder.label} yet
                </div>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)' }}>
                  {canAdd ? 'PDF, DOCX, JPG or PNG up to 25 MB' : 'Receipts, warranties and reports will appear here'}
                </div>
              </div>
            ) : view === 'grid' ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 120 : zoom}px, 1fr))`,
                  gap: isMobile ? 16 : wide ? 28 : 24,
                }}>
                {canAdd && staged && (
                  <div style={{ minWidth: 0 }}>
                    {/* the new file as paper: uploading, then named and filed */}
                    <div style={{ position: 'relative', aspectRatio: '3 / 4' }}>
                      {paper({ id: 'staged', name: staged.file.name })}
                      {!staged.docId && (
                        <div style={{ position: 'absolute', left: 12, right: 12, bottom: 14 }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: '#2b2a28', marginBottom: 6 }}>Uploading…</div>
                          <div style={{ height: 3, borderRadius: 2, background: 'rgba(38,38,37,0.14)', overflow: 'hidden' }}>
                            <div
                              style={{
                                height: '100%',
                                width: `${Math.max(6, upload.uploadProgress)}%`,
                                background: '#2b2a28',
                                transition: 'width 0.2s ease',
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        aria-label="Cancel"
                        onClick={() => setStaged(null)}
                        style={{ ...iconBtn, position: 'absolute', top: 6, right: 6, width: 26, height: 26, color: '#2b2a28' }}>
                        <X size={14} />
                      </button>
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        autoFocus
                        aria-label="File name"
                        value={staged.name}
                        onChange={(e) => setStaged({ ...staged, name: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && addStaged()}
                        style={{
                          height: 30,
                          padding: '0 9px',
                          border: 0,
                          borderRadius: 8,
                          outline: 'none',
                          background: 'var(--tx-fill-2)',
                          color: 'var(--tx-text)',
                          fontFamily: 'inherit',
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      />
                      <select
                        aria-label="Folder"
                        value={staged.folderId}
                        onChange={(e) => setStaged({ ...staged, folderId: e.target.value })}
                        style={{
                          height: 30,
                          padding: '0 6px',
                          border: 0,
                          borderRadius: 8,
                          outline: 'none',
                          background: 'var(--tx-fill)',
                          color: 'var(--tx-muted)',
                          fontFamily: 'inherit',
                          fontSize: 12,
                        }}>
                        {realFolders.map((rf) => (
                          <option key={rf.id} value={rf.id}>
                            {rf.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={!staged.docId}
                        onClick={addStaged}
                        style={{
                          height: 32,
                          border: 0,
                          borderRadius: 9,
                          background: staged.docId ? 'var(--tx-lime)' : 'var(--tx-fill)',
                          color: staged.docId ? '#262626' : 'var(--tx-faint)',
                          fontFamily: 'inherit',
                          fontSize: 13,
                          fontWeight: 700,
                          letterSpacing: '-0.02em',
                          cursor: staged.docId ? 'pointer' : 'default',
                        }}>
                        {staged.docId ? 'Add' : 'Uploading…'}
                      </button>
                    </div>
                  </div>
                )}
                {canAdd && !staged && (
                  <button
                    type="button"
                    onClick={() => dropzone.open()}
                    className="hx-fw-tile"
                    style={{
                      // a button centres its content vertically by default: top-align it with the papers beside it
                      display: 'flex',
                      flexDirection: 'column',
                      alignSelf: 'start',
                      minWidth: 0,
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}>
                    {/* an empty sleeve: click to pick, or drop a file anywhere on the folder */}
                    <div
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        aspectRatio: '3 / 4',
                        borderRadius: 10,
                        border: '1.5px dashed rgba(247,247,246,0.22)',
                        display: 'grid',
                        placeItems: 'center',
                        color: 'var(--tx-muted)',
                      }}>
                      <span
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          background: 'var(--tx-fill)',
                        }}>
                        <Plus size={18} strokeWidth={1.8} />
                      </span>
                    </div>
                    <div style={{ marginTop: 10, padding: '0 2px' }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-text)' }}>Add a file</span>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)', marginTop: 3 }}>or drop one here</div>
                    </div>
                  </button>
                )}
                {files.map((f) => (
                  <div key={f.id} className="hx-fw-tile" style={{ minWidth: 0 }}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Preview ${f.name}`}
                      onClick={(e) => onPreview(e, f.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onPreview(e as unknown as React.MouseEvent, f.id);
                      }}
                      style={{ position: 'relative', aspectRatio: '3 / 4', cursor: 'pointer' }}>
                      {paper(f)}
                      {/* one fixed-size bar, centred on the sheet whatever the zoom */}
                      <div style={{ position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)' }}>
                        {actions(f, true)}
                      </div>
                    </div>
                    <div style={{ marginTop: 10, padding: '0 2px' }}>
                      {nameNode(f, 12.5)}
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)', marginTop: 3 }}>{fmt(f.createdAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {files.map((f, i) => (
                  <div
                    key={f.id}
                    className="hx-fw-row"
                    onDoubleClick={(e) => onPreview(e, f.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr) 130px auto',
                      gap: 16,
                      alignItems: 'center',
                      minHeight: 54,
                      padding: '6px 10px',
                      borderRadius: 10,
                      borderTop: i ? '1px solid var(--tx-line)' : 'none',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                      <span style={{ width: 30, height: 40, flexShrink: 0 }}>{paper(f, true)}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>{nameNode(f, 13)}</div>
                    </div>
                    {!isMobile && <span style={{ fontSize: 12, color: 'var(--tx-muted)' }}>{fmt(f.createdAt)}</span>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{actions(f, false)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordsBrowser;
